import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

import { API_URL } from '../config/api';

const AuthContext = createContext(null);

// In-memory token storage (secure: not in localStorage, not accessible via XSS)
let accessToken = null;
let refreshTokenValue = null;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add Authorization header to all requests if token exists
axios.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (refreshTokenValue && config.url?.includes('/api/auth/refresh')) {
    config.headers['X-Refresh-Token'] = refreshTokenValue;
  }
  return config;
});

// List of endpoints that should NOT trigger token refresh
const SKIP_REFRESH_ENDPOINTS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/me', '/api/auth/logout', '/api/auth/forgot-password', '/api/auth/reset-password', '/api/auth/reset-token-status'];

// Setup axios interceptor for automatic token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Setup response interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // Skip refresh logic for auth endpoints
    const shouldSkipRefresh = SKIP_REFRESH_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));
    
    // If error is 401, we haven't retried, and it's not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !shouldSkipRefresh) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        // Store the new access token from response body
        if (refreshRes.data?.access_token) {
          accessToken = refreshRes.data.access_token;
        }
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        accessToken = null;
        refreshTokenValue = null;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(false);
      accessToken = null;
      refreshTokenValue = null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    const data = response.data;
    // Store tokens in memory for Authorization header fallback
    if (data.access_token) {
      accessToken = data.access_token;
    }
    if (data.refresh_token) {
      refreshTokenValue = data.refresh_token;
    }
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Logout cleanup - error is non-critical
    } finally {
      accessToken = null;
      refreshTokenValue = null;
      setUser(false);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      if (res.data?.access_token) {
        accessToken = res.data.access_token;
      }
      await checkAuth();
    } catch {
      accessToken = null;
      refreshTokenValue = null;
      setUser(false);
    }
  }, [checkAuth]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    refreshToken,
    checkAuth,
  }), [user, loading, login, logout, refreshToken, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
